import { Moment } from "moment";
import * as moment from "moment";
import { orchestrator } from "satcheljs";
import { runInAction } from "mobx";
import updatePreviewScheduleViewDimensions from "../actions/updatePreviewScheduleViewDimensions";
import { IPreviewSchedulePageStore } from "../store/PreviewSchedulePageStore";
import { INoteEntity, IOpenShiftEntity, IShiftEntity, ScheduleCalendarType } from "../../../../sh-models";
import { getUpdatedDateRange, hasCalendarTypeChanged, hasScheduleRangeChanged, hasSelectedDateChanged } from "../../schedules/lib/orchestrators/updateScheduleViewDimensions";
import { clearScheduleDataInView, setIsConflictCalculationInProgress, setScheduleCalendarType, setScheduleSelectedDate, setScheduleSelectedRange, setupConflicts, updateIsDataInDateRangeLoaded, updateNotesInView, updateOpenShiftsInView, updateShiftsInView } from "../../schedules/lib";
import { TeamDataService } from "../../../../sh-services";
import { TeamStore } from "../../../../sh-stores";
import { setGlobalMessageFromException } from "../../..";
import DateUtils from "../../../utility/DateUtils";
import { setupPreviewSchedulePageStates } from "../mutators/uiMutators";
import previewSchedulePageStore from "../store/store";

/**
 * Core logic for updating preview schedule view dimensions and loading data.
 * Exported so it can be called directly and awaited in initialization flows.
 */
export async function updatePreviewScheduleViewDimensionsCore(
    viewState: IPreviewSchedulePageStore,
    selectedDate: Moment | null,
    scheduleCalendarType: ScheduleCalendarType | null,
    isTeamBootstrap: boolean
): Promise<void> {
    if (!viewState) {
        return;
    }
    // Store previous view dimensions
    const previousViewStart: Moment = viewState.viewStartDate ? viewState.viewStartDate : null;
    const previousViewEnd: Moment = viewState.viewEndDate ? viewState.viewEndDate : null;
    const previousSelectedDate: Moment = viewState.viewSelectedDate ? viewState.viewSelectedDate : null;
    const previousCalendarType: ScheduleCalendarType = viewState.scheduleCalendarType;
    const updatedCalendarType: ScheduleCalendarType = scheduleCalendarType || previousCalendarType;
    const updatedSelectedDate: Moment = selectedDate || previousSelectedDate;
    const { updatedViewStart, updatedViewEnd } = getUpdatedDateRange(updatedCalendarType, updatedSelectedDate);

    setScheduleSelectedRange(viewState, updatedViewStart, updatedViewEnd);

    try {
        let notes: INoteEntity[] = [];
        let shifts: IShiftEntity[] = [];
        let openShifts: IOpenShiftEntity[] = [];

        const isScheduleRangeChanged = hasScheduleRangeChanged(previousViewStart, previousViewEnd, updatedViewStart, updatedViewEnd);
        const shouldLoadScheduleData: boolean = isScheduleRangeChanged || !viewState.isDataInDateRangeLoaded;

        if (shouldLoadScheduleData) {
            updateIsDataInDateRangeLoaded(false, viewState);
            setIsConflictCalculationInProgress(true, viewState);
        }

        runInAction(() => {
            if (hasCalendarTypeChanged(previousCalendarType, updatedCalendarType)) {
                setScheduleCalendarType(viewState, updatedCalendarType);
            }
            if (hasSelectedDateChanged(previousSelectedDate, updatedSelectedDate)) {
                setScheduleSelectedDate(viewState, updatedSelectedDate);
            }
        });

        const queryStartTime = isoUTCStringToMoment(previewSchedulePageStore().startDate)?.startOf('day');
        const queryEndTime = isoUTCStringToMoment(previewSchedulePageStore().endDate)?.endOf('day');

        if (shouldLoadScheduleData) {
            try {
                // Use the new getDataInDateRangeForSmartSchedule which filters by a single tag
                // and returns the data directly (no callback needed)
                const dataInRange = await TeamDataService.getDataInDateRangeForSmartSchedule(
                    TeamStore().teamId,
                    queryStartTime,
                    queryEndTime,
                    viewState.selectedTagId,
                    true /* forceLoadFromNetwork */
                );

                notes = dataInRange.notes;
                shifts = dataInRange.shifts;
                openShifts = dataInRange.openShifts;
                const jobId = viewState.jobId;

                const smartScheduleData = await TeamDataService.getPreDraftDataInDateRangeForSmartSchedule(
                    TeamStore().teamId,
                    queryStartTime,
                    queryEndTime,
                    viewState.selectedTagId,
                    jobId
                );

                shifts = shifts.concat(smartScheduleData.shifts);
                openShifts = openShifts.concat(smartScheduleData.openShifts);

            } catch (error) {
                // Show the error message. Do not re-throw the error
                // This shouldn't block setting up the ScheduleViewDimensions which will break date navigations
                setGlobalMessageFromException(error, false /* autoDismiss */);
            }
            runInAction(() => {
                // if our new schedule range is not contiguous with the old, we will clear the existing schedule data
                if (!DateUtils.isRangeContiguous(previousViewStart, previousViewEnd, updatedViewStart, updatedViewEnd)) {
                    clearScheduleDataInView(viewState, false);
                }

                // Enable the data loaded flag since schedule data should now be loaded
                updateIsDataInDateRangeLoaded(true, viewState);
                updateNotesInView(notes, viewState);
                updateShiftsInView(shifts, viewState);
                updateOpenShiftsInView(openShifts, viewState);
            });
        }
        if (isTeamBootstrap) {
            setupPreviewSchedulePageStates(true);
        }
    } catch (error) {
        setGlobalMessageFromException(error, false /* autoDismiss */);
    } finally {
        setupConflicts(
            viewState,
            null /* shiftsAdded */,
            null /* shiftsDeleted */,
            null /* shiftsUpdated */,
            viewState.viewStartDate /* updatedViewStartTime */,
            viewState.viewEndDate /* updatedViewEndTime */,
            false /* force calculate */,
            false /* non-PR mode */,
            viewState.showShiftConflicts /* user settings enabled */);
    }
}

/**
 * Orchestrator that responds to the updatePreviewScheduleViewDimensions action.
 * Delegates to the core function for the actual logic.
 */
export const updatePreviewScheduleViewDimensionsOrchestrator = orchestrator(
    updatePreviewScheduleViewDimensions,
    async (actionMessage: {
        viewState: IPreviewSchedulePageStore,
        selectedDate: Moment | null,
        scheduleCalendarType: ScheduleCalendarType | null,
        isTeamBootstrap: boolean
    }) => {
        const { viewState, selectedDate, scheduleCalendarType, isTeamBootstrap } = actionMessage;
        await updatePreviewScheduleViewDimensionsCore(viewState, selectedDate, scheduleCalendarType, isTeamBootstrap);
    }
);

/**
 * Helper function to convert an ISO string date to a Moment object.
 * treats input string as UTC
 * @param isoString - The ISO date string to convert.
 * @returns Moment object representing the date, or null if input is invalid.
 */
export function isoUTCStringToMoment(isoString: string | null | undefined): Moment | null {
    if (!isoString) {
        return null;
    }
    const m = moment.utc(isoString, moment.ISO_8601, true);
    return m.isValid() ? m : null;
}